import React from "react";
import {
  Card,
  CardContent,
  Typography,
  Skeleton,
  Box,
  Chip,
} from "@mui/material";
import { SvgIconComponent } from "@mui/icons-material";

/**
 * MetricCard Component
 *
 * A flexible, reusable metric card component that supports:
 * - Simple metrics (icon + value + label)
 * - Breakdown metrics (with sub-items and chips)
 * - Gradient or white backgrounds
 * - Skeleton loading state
 * - Hover animations
 *
 * **Features:**
 * - Two layout variants: centered (default) or left-aligned (for breakdowns)
 * - Optional breakdown section with sub-metrics
 * - Optional chip badge
 * - Enterprise-grade gradient styling
 * - Fully responsive
 * - Type-safe props
 *
 * **Common Gradients (from theme.ts):**
 * - `linear-gradient(135deg, #1a237e 0%, #3949ab 100%)` - Navy (tickets)
 * - `linear-gradient(135deg, #f093fb 0%, #f5576c 100%)` - Pink (cars)
 * - `linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)` - Cyan (bikes)
 * - `linear-gradient(135deg, #fa709a 0%, #fee140 100%)` - Gold (revenue)
 * - `linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)` - Green (positive metrics)
 *
 * **Usage Examples:**
 * ```tsx
 * // Simple metric card
 * <MetricCard
 *   icon={DirectionsCar}
 *   label="Total Cars"
 *   value="1,234"
 *   gradient="linear-gradient(135deg, #f093fb 0%, #f5576c 100%)"
 *   isLoading={isLoading}
 * />
 *
 * // Dashboard metric with breakdown
 * <MetricCard
 *   icon={ConfirmationNumber}
 *   label="Tickets"
 *   value={1234}
 *   gradient="linear-gradient(135deg, #1a237e 0%, #3949ab 100%)"
 *   variant="white"
 *   showHeader={true}
 *   breakdownItems={[
 *     { label: "Cars", value: 850 },
 *     { label: "Bikes", value: 384 }
 *   ]}
 *   chipLabel="All Vehicles"
 *   chipColor="#1a237e"
 *   borderColor="rgba(26,35,126,0.3)"
 * />
 * ```
 */

interface BreakdownItem {
  label: string;
  value: string | number;
  /** Optional icon/emoji prefix for the breakdown item */
  prefix?: string;
}

interface MetricCardProps {
  /** MUI icon component to display */
  icon: SvgIconComponent;
  /** Descriptive label for the metric */
  label: string;
  /** The metric value to display (can be string or number) */
  value: string | number;
  /** CSS gradient for card background (or icon gradient if variant="white") */
  gradient: string;
  /** Loading state - shows skeleton when true */
  isLoading?: boolean;
  /** Optional subtitle text below the value */
  subtitle?: string;
  /** Icon size in pixels (default: 48 for gradient cards, 28 for white cards) */
  iconSize?: number;
  /** Minimum card height (default: 160) */
  minHeight?: number;
  /** Optional children to render below the metric */
  children?: React.ReactNode;
  /** Optional border color for card */
  borderColor?: string;
  /** Card variant: "gradient" (centered, for hero sections) or "white" (left-aligned, for dashboards) */
  variant?: "gradient" | "white";
  /** Show icon + title header (Dashboard style, only for variant="white") */
  showHeader?: boolean;
  /** Breakdown items to show below the metric */
  breakdownItems?: BreakdownItem[];
  /** Optional chip label for breakdown section */
  chipLabel?: string;
  /** Chip background color (default: matches gradient) */
  chipColor?: string;
  /** Footer text to show below breakdown (e.g., percentage) */
  footerText?: string;
}

const MetricCard: React.FC<MetricCardProps> = ({
  icon: Icon,
  label,
  value,
  gradient,
  isLoading = false,
  subtitle,
  iconSize,
  minHeight = 160,
  children,
  borderColor,
  variant = "gradient",
  showHeader = false,
  breakdownItems,
  chipLabel,
  chipColor,
  footerText,
}) => {
  // Auto-detect layout: if showHeader or breakdown, use left-aligned Dashboard style
  const isDashboardStyle = variant === "white" || showHeader || breakdownItems;
  const effectiveIconSize = iconSize ?? (isDashboardStyle ? 28 : 48);

  // console.log("[MetricCard] Render -", label, "isLoading:", isLoading);

  return (
    <Card
      elevation={2}
      sx={{
        background: variant === "gradient" ? gradient : "white",
        color: variant === "gradient" ? "white" : "inherit",
        minHeight,
        height: "100%",
        borderRadius: 3,
        border: borderColor ? "1px solid" : "1px solid rgba(0,0,0,0.08)",
        borderColor: borderColor || "rgba(0,0,0,0.08)",
        transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
        "&:hover": {
          transform: "translateY(-4px)",
          boxShadow: 6,
        },
      }}
    >
      <CardContent
        sx={{
          textAlign: isDashboardStyle ? "left" : "center",
          display: "flex",
          flexDirection: "column",
          justifyContent: isDashboardStyle ? "flex-start" : "center",
          height: "100%",
          p: 3,
        }}
      >
        {isLoading ? (
          // Skeleton loading state
          <>
            {showHeader ? (
              // Dashboard-style header skeleton
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}
              >
                <Skeleton
                  variant="rounded"
                  width={56}
                  height={56}
                  sx={{ borderRadius: 1.5 }}
                />
                <Skeleton
                  variant="text"
                  width={120}
                  sx={{ fontSize: "1rem" }}
                />
              </Box>
            ) : (
              // Simple icon skeleton (centered)
              <Skeleton
                variant="circular"
                width={effectiveIconSize}
                height={effectiveIconSize}
                sx={{
                  bgcolor:
                    variant === "gradient"
                      ? "rgba(255,255,255,0.2)"
                      : "rgba(0,0,0,0.11)",
                  margin: isDashboardStyle ? "0 0 12px 0" : "0 auto 12px",
                }}
              />
            )}
            <Skeleton
              variant="text"
              width="60%"
              sx={{
                bgcolor:
                  variant === "gradient"
                    ? "rgba(255,255,255,0.2)"
                    : "rgba(0,0,0,0.11)",
                fontSize: "2.125rem", // Match h4 size
                margin: isDashboardStyle ? "0" : "0 auto",
              }}
            />
            <Skeleton
              variant="text"
              width="80%"
              sx={{
                bgcolor:
                  variant === "gradient"
                    ? "rgba(255,255,255,0.2)"
                    : "rgba(0,0,0,0.11)",
                mt: 1,
                margin: isDashboardStyle ? "8px 0 0 0" : "8px auto 0",
              }}
            />
            {(subtitle || breakdownItems || footerText) && (
              <Box
                sx={{
                  mt: 2,
                  pt: 2,
                  borderTop: 1,
                  borderColor: "divider",
                }}
              >
                <Skeleton
                  variant="text"
                  width="70%"
                  sx={{
                    bgcolor:
                      variant === "gradient"
                        ? "rgba(255,255,255,0.2)"
                        : "rgba(0,0,0,0.11)",
                    fontSize: "0.75rem",
                  }}
                />
                {breakdownItems && breakdownItems.length > 1 && (
                  <Skeleton
                    variant="text"
                    width="60%"
                    sx={{
                      bgcolor:
                        variant === "gradient"
                          ? "rgba(255,255,255,0.2)"
                          : "rgba(0,0,0,0.11)",
                      fontSize: "0.75rem",
                      mt: 0.5,
                    }}
                  />
                )}
              </Box>
            )}
          </>
        ) : (
          // Loaded state
          <>
            {showHeader ? (
              // Dashboard-style header: Icon box + Title
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  mb: 2,
                }}
              >
                <Box
                  sx={{
                    width: 56,
                    height: 56,
                    borderRadius: 1.5,
                    background: gradient,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Icon sx={{ fontSize: effectiveIconSize, color: "white" }} />
                </Box>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ fontWeight: 500, fontSize: "1rem" }}
                >
                  {label}
                </Typography>
              </Box>
            ) : (
              // Simple icon (centered or left-aligned)
              <Icon
                sx={{
                  fontSize: effectiveIconSize,
                  mb: 1,
                  opacity: 0.9,
                }}
              />
            )}

            {/* Main metric value */}
            <Typography
              variant="h4"
              fontWeight="bold"
              sx={{ fontSize: { xs: "1.5rem", sm: "2rem" } }}
            >
              {typeof value === "number" ? value.toLocaleString() : value}
            </Typography>

            {/* Label (shown below value for simple cards, skipped if showHeader=true) */}
            {!showHeader && (
              <Typography
                variant="body2"
                sx={{
                  opacity: 0.9,
                  mt: 1,
                }}
              >
                {label}
              </Typography>
            )}

            {/* Subtitle */}
            {subtitle && (
              <Typography
                variant="caption"
                sx={{
                  opacity: variant === "gradient" ? 0.8 : 0.6,
                  mt: 0.5,
                  display: "block",
                }}
              >
                {subtitle}
              </Typography>
            )}

            {/* Breakdown Section */}
            {(breakdownItems || chipLabel || footerText) && (
              <Box
                sx={{
                  mt: 2,
                  pt: 2,
                  borderTop: 1,
                  borderColor: "divider",
                }}
              >
                {/* Single breakdown item - show as single line */}
                {breakdownItems &&
                breakdownItems.length === 1 &&
                !footerText ? (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <Typography variant="caption" color="text.secondary">
                      {breakdownItems[0].prefix && (
                        <span>{breakdownItems[0].prefix} </span>
                      )}
                      {breakdownItems[0].label}
                    </Typography>
                    <Typography
                      variant="body2"
                      fontWeight={600}
                      sx={{ color: "#10b981" }}
                    >
                      {typeof breakdownItems[0].value === "number"
                        ? breakdownItems[0].value.toLocaleString()
                        : breakdownItems[0].value}
                    </Typography>
                  </Box>
                ) : (
                  /* Multiple breakdown items with optional chip or footer text */
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "flex-start",
                    }}
                  >
                    {/* Breakdown Items */}
                    {breakdownItems && breakdownItems.length > 0 && (
                      <Box sx={{ flex: 1 }}>
                        {breakdownItems.map((item, index) => (
                          <Typography
                            key={index}
                            variant="caption"
                            color="text.secondary"
                            display="block"
                            sx={{
                              mb: index < breakdownItems.length - 1 ? 0.5 : 0,
                            }}
                          >
                            {item.prefix && <span>{item.prefix} </span>}
                            {item.label}:{" "}
                            {typeof item.value === "number"
                              ? item.value.toLocaleString()
                              : item.value}
                          </Typography>
                        ))}
                      </Box>
                    )}

                    {/* Chip Badge */}
                    {chipLabel && (
                      <Chip
                        label={chipLabel}
                        size="small"
                        sx={{
                          bgcolor: chipColor || gradient,
                          color: "white",
                          fontWeight: 600,
                          border: "none",
                        }}
                      />
                    )}
                  </Box>
                )}

                {/* Footer Text (for percentages or additional info) */}
                {footerText && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    display="block"
                    sx={{
                      mt: breakdownItems && breakdownItems.length > 0 ? 0 : 0,
                    }}
                  >
                    {footerText}
                  </Typography>
                )}
              </Box>
            )}

            {/* Custom children (for additional content) */}
            {children && <Box sx={{ mt: 2 }}>{children}</Box>}
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default React.memo(MetricCard);
